import { getDefaultRouteForRole } from 'src/routes/defaultRouteByRole';
import { getStoredAuthValue } from './authStorage';

export const SIGNUP_TOKEN_STORAGE_KEY = 'quotly_signup_token';

const hasWindow = () => typeof window !== 'undefined';

const hasValidTenantId = (tenantId) => {
  const value = String(tenantId ?? '').trim();
  return value !== '' && value.toLowerCase() !== 'null' && value.toLowerCase() !== 'undefined';
};

export const getStoredSignupToken = () => {
  if (!hasWindow()) return '';
  return (
    window.localStorage.getItem(SIGNUP_TOKEN_STORAGE_KEY)
    || window.sessionStorage.getItem(SIGNUP_TOKEN_STORAGE_KEY)
    || ''
  );
};

export const storeSignupToken = (token) => {
  if (!hasWindow()) return;
  window.localStorage.setItem(SIGNUP_TOKEN_STORAGE_KEY, token);
  window.sessionStorage.setItem(SIGNUP_TOKEN_STORAGE_KEY, token);
};

export const clearSignupToken = () => {
  if (!hasWindow()) return;
  window.localStorage.removeItem(SIGNUP_TOKEN_STORAGE_KEY);
  window.sessionStorage.removeItem(SIGNUP_TOKEN_STORAGE_KEY);
};

export const persistOnboardingState = (user = {}) => {
  if (!hasWindow()) return;

  if (Object.prototype.hasOwnProperty.call(user, 'onboardingStep')) {
    window.localStorage.setItem('onboardingStep', user.onboardingStep || '');
  }
  if (Object.prototype.hasOwnProperty.call(user, 'isOnboardingComplete')) {
    window.localStorage.setItem('isOnboardingComplete', String(Boolean(user.isOnboardingComplete)));
  }
  if (Object.prototype.hasOwnProperty.call(user, 'isBusinessVerified')) {
    window.localStorage.setItem('isBusinessVerified', String(Boolean(user.isBusinessVerified)));
  }
  if (Object.prototype.hasOwnProperty.call(user, 'isEmailVerified')) {
    window.localStorage.setItem('isEmailVerified', String(Boolean(user.isEmailVerified)));
  }
};

const resolveStoredBoolean = (value) => {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (!normalized) return null;
  if (normalized === 'true') return true;
  if (normalized === 'false') return false;
  return null;
};

export const persistLoggedInUser = ({ data, setUser }) => {
  if (data?.tokens?.access?.token) {
    localStorage.setItem('accessToken', data.tokens.access.token);
  }
  if (data?.tokens?.refresh?.token) {
    localStorage.setItem('refreshToken', data.tokens.refresh.token);
  }
  if (data?.user?.id) {
    localStorage.setItem('userID', data.user.id);
  }
  if (data?.user?.role) {
    localStorage.setItem('userRole', data.user.role);
  }

  if (hasValidTenantId(data?.user?.tenantId)) {
    localStorage.setItem('tenantID', data.user.tenantId);
  } else {
    localStorage.removeItem('tenantID');
  }

  persistOnboardingState(data?.user || {});

  if (typeof setUser === 'function' && data?.user) {
    const nextUser = {
      id: data.user.id,
      name: data.user.name,
      number: data.user.number,
      role: data.user.role,
      email: data.user.email,
      tenantId: data.user.tenantId || '',
    };

    if (Object.prototype.hasOwnProperty.call(data.user, 'onboardingStep')) {
      nextUser.onboardingStep = data.user.onboardingStep;
    }
    if (Object.prototype.hasOwnProperty.call(data.user, 'isOnboardingComplete')) {
      nextUser.isOnboardingComplete = Boolean(data.user.isOnboardingComplete);
    }
    if (Object.prototype.hasOwnProperty.call(data.user, 'isBusinessVerified')) {
      nextUser.isBusinessVerified = Boolean(data.user.isBusinessVerified);
    }
    if (Object.prototype.hasOwnProperty.call(data.user, 'isEmailVerified')) {
      nextUser.isEmailVerified = Boolean(data.user.isEmailVerified);
    }
    if (Object.prototype.hasOwnProperty.call(data.user, 'tourSeen')) {
      nextUser.tourSeen = Boolean(data.user.tourSeen);
    }

    setUser(nextUser);
  }
};

export const getOnboardingRedirectPath = (user = {}) => {
  const tenantId = user?.tenantId ?? getStoredAuthValue('tenantID');
  const isEmailVerified = Object.prototype.hasOwnProperty.call(user, 'isEmailVerified')
    ? Boolean(user.isEmailVerified)
    : resolveStoredBoolean(getStoredAuthValue('isEmailVerified'));

  if (isEmailVerified === false) {
    return '/auth/verify-email';
  }

  if (!hasValidTenantId(tenantId)) {
    return '/auth/register/company';
  }

  return null;
};

export const getPostSignupRedirect = (user) =>
  getOnboardingRedirectPath(user) || getDefaultRouteForRole(user?.role, null) || '/';

export const loadRazorpayCheckout = () =>
  new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('Razorpay checkout is not available'));
      return;
    }

    if (window.Razorpay) {
      resolve(window.Razorpay);
      return;
    }

    const existingScript = document.querySelector('script[data-razorpay-checkout="true"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.Razorpay));
      existingScript.addEventListener('error', () => reject(new Error('Failed to load Razorpay checkout')));
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.dataset.razorpayCheckout = 'true';
    script.onload = () => resolve(window.Razorpay);
    script.onerror = () => reject(new Error('Failed to load Razorpay checkout'));
    document.body.appendChild(script);
  });
